// The endurance sports and what each one logs: its variants, whether pace or speed is the natural measure,
// whether elevation matters, and its own fields (pool length and stroke for swimming, trail difficulty for
// mountain biking). One table, used by the catalog, the log form, the import, Progress and the MCP coach.
import type { ActivityKind, CardioSport, Exercise } from '../types';

/**
 * How speed is shown: pace per km (running, walking, hiking, skiing), pace per 100 m (swimming), pace per
 * 500 m (rowing, the rowing machine's own measure) or km/h (cycling, inline skating, elliptical).
 */
export type SpeedMeasure = 'pace_km' | 'pace_100m' | 'pace_500m' | 'speed';

export interface SportInfo {
  label: string;
  /** Variant id to label. The first is the default. */
  variants: Record<string, string>;
  measure: SpeedMeasure;
  /** Elevation gain is worth logging (hiking, trail running, MTB, ski touring). */
  elevation: boolean;
  /** Distances are shown in m (swimming, rowing intervals), else in km. */
  meters: boolean;
  /** Variants done indoors. */
  indoorVariants: string[];
  /** Extra fields the log form shows. */
  fields: Array<'poolLength' | 'stroke' | 'trailDifficulty'>;
  /** The catalog exercise a log of this sport and variant counts for (Progress per exercise, new-exercise bonus). */
  exercise: Record<string, string>;
  /** Typical speed in km/h, to plan a distance block's time when no pace is given. */
  typicalKmh: number;
}

export const CARDIO_SPORTS: Record<CardioSport, SportInfo> = {
  running: {
    label: 'Running', variants: { road: 'Road', trail: 'Trail', track: 'Track', treadmill: 'Treadmill' }, measure: 'pace_km',
    elevation: true, meters: false, indoorVariants: ['treadmill'], fields: [],
    exercise: { road: 'run', trail: 'trail_run', track: 'track_run', treadmill: 'treadmill_run' }, typicalKmh: 10,
  },
  walking: {
    label: 'Walking', variants: { outdoor: 'Outdoor', nordic: 'Nordic walking', treadmill: 'Treadmill' }, measure: 'pace_km',
    elevation: false, meters: false, indoorVariants: ['treadmill'], fields: [],
    exercise: { outdoor: 'walk', nordic: 'nordic_walk', treadmill: 'incline_walk' }, typicalKmh: 5,
  },
  hiking: {
    label: 'Hiking', variants: { trail: 'Trail', mountain: 'Mountain' }, measure: 'pace_km',
    elevation: true, meters: false, indoorVariants: [], fields: [],
    exercise: { trail: 'hike', mountain: 'hike' }, typicalKmh: 4,
  },
  cycling: {
    label: 'Cycling', variants: { road: 'Road', gravel: 'Gravel', indoor: 'Indoor trainer' }, measure: 'speed',
    elevation: true, meters: false, indoorVariants: ['indoor'], fields: [],
    exercise: { road: 'road_ride', gravel: 'gravel_ride', indoor: 'indoor_ride' }, typicalKmh: 25,
  },
  mountain_biking: {
    label: 'Mountain biking', variants: { trail: 'Trail', enduro: 'Enduro', cross_country: 'Cross-country' }, measure: 'speed',
    elevation: true, meters: false, indoorVariants: [], fields: ['trailDifficulty'],
    exercise: { trail: 'mtb_ride', enduro: 'mtb_ride', cross_country: 'mtb_ride' }, typicalKmh: 14,
  },
  swimming: {
    label: 'Swimming', variants: { pool: 'Pool', open_water: 'Open water' }, measure: 'pace_100m',
    elevation: false, meters: true, indoorVariants: ['pool'], fields: ['poolLength', 'stroke'],
    exercise: { pool: 'pool_swim', open_water: 'open_water_swim' }, typicalKmh: 2.5,
  },
  rowing: {
    label: 'Rowing', variants: { machine: 'Rowing machine', water: 'On the water' }, measure: 'pace_500m',
    elevation: false, meters: true, indoorVariants: ['machine'], fields: [],
    exercise: { machine: 'rowing_machine', water: 'water_row' }, typicalKmh: 12,
  },
  elliptical: {
    label: 'Elliptical', variants: { machine: 'Machine' }, measure: 'speed',
    elevation: false, meters: false, indoorVariants: ['machine'], fields: [],
    exercise: { machine: 'elliptical' }, typicalKmh: 8,
  },
  skiing: {
    label: 'Skiing', variants: { classic: 'Cross-country, classic', skate: 'Cross-country, skate', touring: 'Ski touring' }, measure: 'pace_km',
    elevation: true, meters: false, indoorVariants: [], fields: [],
    exercise: { classic: 'xc_ski_classic', skate: 'xc_ski_skate', touring: 'ski_touring' }, typicalKmh: 10,
  },
  inline_skating: {
    label: 'Inline skating', variants: { outdoor: 'Outdoor' }, measure: 'speed',
    elevation: false, meters: false, indoorVariants: [], fields: [],
    exercise: { outdoor: 'inline_skating' }, typicalKmh: 18,
  },
  stair_climbing: {
    label: 'Stair climbing', variants: { machine: 'Stair machine', stairs: 'Real stairs' }, measure: 'speed',
    elevation: true, meters: false, indoorVariants: ['machine'], fields: [],
    exercise: { machine: 'stair_climber', stairs: 'stair_climber' }, typicalKmh: 3,
  },
  jump_rope: {
    label: 'Jump rope', variants: { basic: 'Basic' }, measure: 'speed',
    elevation: false, meters: false, indoorVariants: [], fields: [],
    exercise: { basic: 'jump_rope_basic' }, typicalKmh: 0,
  },
};

export const CARDIO_SPORT_IDS = Object.keys(CARDIO_SPORTS) as CardioSport[];

export function isCardioSport(v: unknown): v is CardioSport {
  return typeof v === 'string' && Object.hasOwn(CARDIO_SPORTS, v);
}

/** The sport's default variant when none (or an unknown one) is given. */
export function sportVariant(sport: CardioSport, variant?: string): string {
  const info = CARDIO_SPORTS[sport];
  return variant && Object.hasOwn(info.variants, variant) ? variant : Object.keys(info.variants)[0]!;
}

/** The catalog exercise a log of this sport counts for. */
export function sportExerciseId(sport: CardioSport, variant?: string): string {
  return CARDIO_SPORTS[sport].exercise[sportVariant(sport, variant)]!;
}

/** "Running · Trail", "Swimming · Pool". */
export function sportLabel(sport: CardioSport, variant?: string): string {
  const info = CARDIO_SPORTS[sport];
  const v = variant && info.variants[variant];
  return v && Object.keys(info.variants).length > 1 ? `${info.label} · ${v}` : info.label;
}

export const ACTIVITY_LABELS: Record<ActivityKind, string> = {
  strength: 'Strength and conditioning',
  cardio: 'Endurance',
  mobility: 'Mobility',
  coordination: 'Coordination and balance',
};

/**
 * The activity kind of an exercise: its own field, else from the sport and category (a sport means cardio;
 * stretches, mobility and breathing are mobility; coordination drills are coordination; everything else is
 * strength, also HIIT moves like burpees, which are logged in reps or time).
 */
export function activityKind(e: Pick<Exercise, 'activity' | 'sport' | 'category'>): ActivityKind {
  if (e.activity) return e.activity;
  if (e.sport) return 'cardio';
  if (e.category === 'mobility' || e.category === 'stretch' || e.category === 'breathing') return 'mobility';
  if (e.category === 'coordination') return 'coordination';
  return 'strength';
}

/** Mountain bike trail difficulty: the singletrail scale (S0 to S5) or the trail park colors. */
export const TRAIL_DIFFICULTY: Record<string, string> = {
  S0: 'S0 · easy, no obstacles', S1: 'S1 · small roots and stones', S2: 'S2 · bigger roots, steps, steeper',
  S3: 'S3 · blocks, tight turns', S4: 'S4 · very steep, technical', S5: 'S5 · extreme',
  green: 'Green · easy', blue: 'Blue · moderate', red: 'Red · difficult', black: 'Black · expert',
};

export const SWIM_STROKES: Record<string, string> = {
  freestyle: 'Freestyle', breaststroke: 'Breaststroke', backstroke: 'Backstroke', butterfly: 'Butterfly', mixed: 'Mixed',
};
